import React, { useState, useMemo } from 'react';
import { rankBets, buildParlays, suggestStake, buildPlan, RISK } from '../scout';
import './scout.css';

// The secret report (type "bardown"): the day's best bets across every sport and market.
const SPORTS = [
  { id: 'all', en: 'All', fr: 'Tous' },
  { id: 'nhl', en: 'NHL', fr: 'LNH' },
  { id: 'nba', en: 'NBA', fr: 'NBA' },
  { id: 'mlb', en: 'MLB', fr: 'MLB' },
  { id: 'nfl', en: 'NFL', fr: 'NFL' },
  { id: 'f1', en: 'F1', fr: 'F1' },
];
const MODES = [
  { id: 'value', en: 'Value', fr: 'Valeur', hint: { en: 'The scout likes it more than the odds do', fr: 'Le scout l\'aime plus que la cote' } },
  { id: 'safest', en: 'Safest', fr: 'Plus sûrs', hint: { en: 'Highest win chance (odds 1.40+)', fr: 'Meilleures chances (cote 1,40+)' } },
  { id: 'long', en: 'Long shots', fr: 'Gros coups', hint: { en: 'Odds 5.00–30.00, best chance first', fr: 'Cote 5,00–30,00, meilleures chances d\'abord' } },
  { id: 'parlays', en: 'Parlays', fr: 'Combinés', hint: { en: 'Ready-made combos — one leg per game', fr: 'Combinés tout prêts — une sélection par match' } },
];
const MARKET = {
  winner: ['Moneyline', 'Gagnant'], puckline: ['Puck line', 'Écart'], runline: ['Run line', 'Écart'], spread: ['Spread', 'Écart'], total: ['Total', 'Total'],
  team_first: ['Scores first', 'Marque en premier'], both_score: ['Both score', 'Les deux marquent'], period_winner: ['Period', 'Période'],
  overtime: ['Overtime', 'Prolongation'], shutout: ['Shutout', 'Blanchissage'], fighting: ['Fight', 'Bagarre'], penalty_mins: ['Penalty mins', 'Pénalités'],
  total_shots: ['Total shots', 'Tirs'], exact_score: ['Correct score', 'Score exact'], first_goal: ['First goal', 'Premier but'],
  anytime_goal: ['Goal scorer', 'Buteur'], winning_goal: ['Winning goal', 'But gagnant'], player_points: ['Player points', 'Points joueur'], player_shots: ['Player shots', 'Tirs joueur'],
  mlb_team_hr: ['Team HR', 'Circuits équipe'], mlb_pitcher_ks: ['Strikeouts', 'Retraits'], mlb_batter_hr: ['Batter HR', 'Circuit'], mlb_batter_rbi: ['RBI', 'Points produits'],
  mlb_batter_sb: ['Stolen bases', 'Buts volés'], mlb_batter_1b: ['Singles', 'Simples'], mlb_batter_2b: ['Doubles', 'Doubles'], mlb_batter_3b: ['Triples', 'Triples'],
  mlb_batter_ab: ['At-bats', 'Présences'], mlb_batter_cycle: ['Cycle', 'Cycle'],
  nba_team_total: ['Team total', 'Total équipe'], nba_quarter_winner: ['Quarter / half', 'Quart / demie'], nba_half_total: ['1st half total', 'Total 1re demie'],
  nba_margin: ['Winning margin', 'Écart final'], nba_overtime: ['Overtime', 'Prolongation'], nba_odd_even: ['Odd / even', 'Pair / impair'], nba_both_110: ['Both 110+', 'Les deux 110+'],
  nba_player_pts: ['Points', 'Points'], nba_player_reb: ['Rebounds', 'Rebonds'], nba_player_ast: ['Assists', 'Passes'], nba_player_3pm: ['3-pointers', 'Tirs de 3'],
  nba_player_stl: ['Steals', 'Interceptions'], nba_player_blk: ['Blocks', 'Contres'], nba_player_pra: ['Pts+Reb+Ast', 'Pts+Reb+Pass'],
  nba_double_double: ['Double-double', 'Double-double'], nba_triple_double: ['Triple-double', 'Triple-double'], nba_top_scorer: ['Top scorer', 'Meilleur marqueur'], nba_first_basket: ['First basket', 'Premier panier'],
  nfl_half_winner: ['1st half', '1re demie'], nfl_both_20: ['Both 20+', 'Les deux 20+'], nfl_overtime: ['Overtime', 'Prolongation'], nfl_anytime_td: ['Touchdown', 'Touché'],
  nfl_first_td: ['First TD', 'Premier touché'], nfl_pass_yds: ['Passing yds', 'Verges passe'], nfl_rush_yds: ['Rushing yds', 'Verges au sol'], nfl_rec_yds: ['Receiving yds', 'Verges réception'],
  f1_winner: ['Race winner', 'Vainqueur'], f1_top3: ['Podium', 'Podium'], f1_top6: ['Top 6', 'Top 6'], f1_top10: ['Top 10', 'Top 10'], f1_pole: ['Pole', 'Pole'],
  f1_fastest_lap: ['Fastest lap', 'Tour rapide'], f1_h2h: ['Teammate duel', 'Duel coéquipiers'], f1_constructor: ['Winning team', 'Équipe gagnante'],
};
const SPORT_TAG = { nhl: 'NHL', nba: 'NBA', mlb: 'MLB', nfl: 'NFL', f1: 'F1' };
const pct = (x) => `${Math.round(x * 100)}%`;

const RISKS = [
  { id: 'careful', en: 'Careful', fr: 'Prudent' },
  { id: 'normal', en: 'Normal', fr: 'Normal' },
  { id: 'bold', en: 'Bold', fr: 'Audacieux' },
];
const loadRisk = () => { try { return localStorage.getItem('scoutRisk') || 'normal'; } catch (e) { return 'normal'; } };

// the track record: each list's graded picks (scout_picks, last 30 days) as if $10 had been bet on each
const RECORD_LISTS = [
  { id: 'value', en: 'Value', fr: 'Valeur' },
  { id: 'safest', en: 'Safest', fr: 'Plus sûrs' },
  { id: 'long', en: 'Long shots', fr: 'Gros coups' },
  { id: 'plan', en: "Today's plan", fr: 'Plan du jour' },
];
const FLAT = 10;
function recordOf(rows, list) {
  const r = { won: 0, lost: 0, pending: 0, profit: 0 };
  rows.filter((x) => x.list === list).forEach((x) => {
    if (x.result === 'won') { r.won++; r.profit += FLAT * (Number(x.odds) - 1); }
    else if (x.result === 'lost') { r.lost++; r.profit -= FLAT; }
    else if (x.result == null) r.pending++;
  });
  return r;
}

export default function ScoutReport({ lang, bets, loading, error, generatedAt, onRefresh, onBack, onAdd, onAddParlay, onAddPlan, describe, slipKeys, balance, money, record }) {
  const [mode, setMode] = useState('value');
  const [risk, setRiskState] = useState(loadRisk);
  const setRisk = (r) => { setRiskState(r); try { localStorage.setItem('scoutRisk', r); } catch (e) { /* private mode */ } };
  const [sport, setSport] = useState('all');
  const L = (en, fr) => (lang === 'fr' ? fr : en);

  const counts = useMemo(() => {
    const c = { all: bets.length };
    bets.forEach((b) => { c[b.sport] = (c[b.sport] || 0) + 1; });
    return c;
  }, [bets]);
  const gamesCount = useMemo(() => new Set(bets.map((b) => String(b.game.id))).size, [bets]);
  const list = useMemo(() => (mode === 'parlays' ? [] : rankBets(bets, mode, sport)), [bets, mode, sport]);
  const parlays = useMemo(() => (mode === 'parlays' ? buildParlays(bets, sport) : []), [bets, mode, sport]);
  const plan = useMemo(() => buildPlan(bets, balance, risk, sport), [bets, balance, risk, sport]);
  const stakeOf = (b, kind) => suggestStake(b, balance, risk, kind);
  const fmtTime = (g) => {
    const d = new Date(g.game_time || g.race_time || 0);
    if (!d.getTime()) return '';
    return d.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  };
  const gameLine = (b) => (b.sport === 'f1' ? b.game.name : `${b.game.away_abbrev || b.game.away_team} @ ${b.game.home_abbrev || b.game.home_team}`);
  const modeInfo = MODES.find((m) => m.id === mode);

  return (
    <div className="sr">
      <header className="sr-head">
        <div>
          <div className="sr-kicker">{L('Secret report', 'Rapport secret')}</div>
          <h2 className="sr-title">Bardown Scout</h2>
          <p className="sr-sub">
            {error && !bets.length ? L('Scout analysis unavailable — odds-only picks.', 'Analyse du scout indisponible — choix selon les cotes.')
              : `${bets.length.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')} ${L('bets scanned', 'paris analysés')} · ${gamesCount} ${L(gamesCount === 1 ? 'game' : 'games', gamesCount === 1 ? 'match' : 'matchs')}${generatedAt ? ` · ${L('scout', 'scout')} ${new Date(generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`}
          </p>
        </div>
        <div className="sr-head-btns">
          <button type="button" className="sr-btn" onClick={onRefresh} disabled={loading}>{loading ? L('Scouting…', 'Analyse…') : L('Refresh', 'Rafraîchir')}</button>
          <button type="button" className="sr-btn sr-btn--ghost" onClick={onBack}>{L('← Games', '← Matchs')}</button>
        </div>
      </header>

      <section className="sr-wallet">
        <div className="sr-wallet-row">
          <span className="sr-wallet-bal">{L('Balance', 'Solde')} <strong>{money(balance)}</strong></span>
          <div className="sr-risk" role="group" aria-label={L('Risk level', 'Niveau de risque')}>
            {RISKS.map((x) => (
              <button key={x.id} type="button" className={`sr-risk-btn${risk === x.id ? ' on' : ''}`} aria-pressed={risk === x.id} onClick={() => setRisk(x.id)}>{L(x.en, x.fr)}</button>
            ))}
          </div>
        </div>
        <p className="sr-wallet-note">
          {L(`Daily budget ${money(balance * RISK[risk].budget)} (${Math.round(RISK[risk].budget * 100)}% of your balance) · 1 unit = ${money(balance * RISK[risk].unit)}`,
            `Budget du jour ${money(balance * RISK[risk].budget)} (${Math.round(RISK[risk].budget * 100)} % de votre solde) · 1 unité = ${money(balance * RISK[risk].unit)}`)}
        </p>
        {plan && (
          <div className="sr-plan">
            <div className="sr-plan-head">
              <span className="sr-plan-title">{L("Today's plan", 'Plan du jour')}</span>
              <span className="sr-plan-sum">{L(`Bet ${money(plan.total)} · returns up to ${money(plan.toWin)}`, `Mise ${money(plan.total)} · rapporte jusqu'à ${money(plan.toWin)}`)}</span>
            </div>
            <ul className="sr-plan-list">
              {plan.legs.map((b) => (
                <li key={b.key}>
                  <span className="sr-main">
                    <span className="sr-meta"><b className="sr-sport">{SPORT_TAG[b.sport]}</b> {gameLine(b)} · {fmtTime(b.game)}</span>
                    <span className="sr-desc">{describe(b)}</span>
                  </span>
                  <span className="sr-plan-stake">{money(b.stake)}<small>@ {b.odds.toFixed(2)}</small></span>
                </li>
              ))}
            </ul>
            <button type="button" className="sr-btn sr-plan-add" onClick={() => onAddPlan(plan.legs)}>{L('Add the whole plan to my slip', 'Ajouter tout le plan au panier')}</button>
          </div>
        )}
      </section>

      <section className="sr-record">
        <div className="sr-plan-head">
          <span className="sr-plan-title">{L('Track record', 'Bilan')}</span>
          <span className="sr-plan-sum">{L(`Last 30 days · $${FLAT} on every pick`, `30 derniers jours · ${FLAT} $ sur chaque choix`)}</span>
        </div>
        <div className="sr-record-grid">
          {RECORD_LISTS.map((x) => {
            const r = recordOf(record || [], x.id);
            const n = r.won + r.lost;
            return (
              <div key={x.id} className="sr-record-cell">
                <span className="sr-record-name">{L(x.en, x.fr)}</span>
                <span className="sr-record-wl">{r.won}–{r.lost}{n ? <small> · {Math.round((r.won / n) * 100)}%</small> : null}</span>
                <span className={`sr-record-pl${r.profit > 0 ? ' up' : r.profit < 0 ? ' down' : ''}`}>{n ? `${r.profit >= 0 ? '+' : '−'}${money(Math.abs(r.profit))}` : L('No results yet', 'Aucun résultat')}</span>
                {r.pending > 0 && <span className="sr-record-pend">{r.pending} {L('pending', 'en attente')}</span>}
              </div>
            );
          })}
        </div>
      </section>

      <div className="sr-modes" role="tablist">
        {MODES.map((m) => (
          <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} className={`sr-mode${mode === m.id ? ' on' : ''}`} onClick={() => setMode(m.id)}>
            {L(m.en, m.fr)}
          </button>
        ))}
      </div>
      <p className="sr-hint">{L(modeInfo.hint.en, modeInfo.hint.fr)}</p>

      <div className="sr-sports">
        {SPORTS.map((s) => (
          <button key={s.id} type="button" className={`sr-chip${sport === s.id ? ' on' : ''}`} onClick={() => setSport(s.id)} disabled={s.id !== 'all' && !counts[s.id]}>
            {L(s.en, s.fr)}{counts[s.id] ? <span className="sr-chip-n">{counts[s.id]}</span> : null}
          </button>
        ))}
      </div>

      {mode === 'parlays' && !(loading && !bets.length) ? (
        parlays.length === 0 ? (
          <div className="sr-empty">{L('Not enough games to build parlays right now.', 'Pas assez de matchs pour bâtir des combinés pour le moment.')}</div>
        ) : (
          <div className="sr-parlays">
            {parlays.map((p) => (
              <section key={p.id} className="sr-parlay">
                <div className="sr-parlay-head">
                  <span className="sr-parlay-name">{L(p.en, p.fr)} <span className="sr-parlay-legs">· {p.legs.length} {L('legs', 'sélections')}</span></span>
                  <span className="sr-odds">{p.odds.toFixed(2)}</span>
                </div>
                <ul className="sr-parlay-list">
                  {p.legs.map((b) => (
                    <li key={b.key}>
                      <span className="sr-main">
                        <span className="sr-meta"><b className="sr-sport">{SPORT_TAG[b.sport]}</b> {gameLine(b)} · {fmtTime(b.game)}</span>
                        <span className="sr-desc">{describe(b)}</span>
                        <span className="sr-market">{L(...(MARKET[b.betType] || [b.betType, b.betType]))}</span>
                      </span>
                      <span className="sr-leg-odds">{b.odds.toFixed(2)}<small>{pct(b.chance)}</small></span>
                    </li>
                  ))}
                </ul>
                <div className="sr-parlay-foot">
                  <span className="sr-chance">{pct(p.chance)} {L('chance all legs win', 'de chances que tout passe')} · {L('Bet', 'Misez')} {money(stakeOf(p, 'parlay'))} → {money(stakeOf(p, 'parlay') * p.odds)}</span>
                  <button type="button" className="sr-btn" onClick={() => onAddParlay(p.legs, stakeOf(p, 'parlay'))}>{L('Add parlay to slip', 'Ajouter le combiné')}</button>
                </div>
              </section>
            ))}
          </div>
        )
      ) : loading && !bets.length ? (
        <div className="sr-empty"><div className="sr-spinner" />{L('Scouting every game… first load can take 20–30 seconds.', 'Analyse de tous les matchs… le premier chargement peut prendre 20 à 30 secondes.')}</div>
      ) : list.length === 0 ? (
        <div className="sr-empty">
          {mode === 'value'
            ? L('No value bets right now — the scout agrees with the odds. Try Safest or Long shots.', 'Aucun pari de valeur pour l\'instant — le scout est d\'accord avec les cotes. Essayez Plus sûrs ou Gros coups.')
            : L('No upcoming games to scout.', 'Aucun match à venir à analyser.')}
        </div>
      ) : (
        <ol className="sr-list">
          {list.map((b, i) => {
            const added = slipKeys.has(b.key);
            return (
              <li key={b.key}>
                <button type="button" className={`sr-bet${added ? ' added' : ''}`} onClick={() => onAdd(b, stakeOf(b))} disabled={added} aria-label={`${describe(b)} @ ${b.odds.toFixed(2)}`}>
                  <span className="sr-rank">{i + 1}</span>
                  <span className="sr-main">
                    <span className="sr-meta"><b className="sr-sport">{SPORT_TAG[b.sport]}</b> {gameLine(b)} · {fmtTime(b.game)}</span>
                    <span className="sr-desc">{describe(b)}</span>
                    <span className="sr-market">{L(...(MARKET[b.betType] || [b.betType, b.betType]))}</span>
                    <span className="sr-bar" aria-hidden="true"><span style={{ width: pct(b.chance) }} /></span>
                    <span className="sr-chance">
                      {pct(b.chance)} {L('win chance', 'de chances')}
                      {b.vegas != null && <> · Vegas {pct(b.vegas)}</>}
                      {b.scout != null && <> · {L('site odds', 'cote du site')} {pct(b.prob)}</>}
                    </span>
                  </span>
                  <span className="sr-side">
                    <span className="sr-odds">{b.odds.toFixed(2)}</span>
                    {b.value != null && b.value > 0 && <span className="sr-value">+{Math.round(b.value * 100)}%</span>}
                    {stakeOf(b) > 0 && <span className="sr-stake">{L('Bet', 'Misez')} {money(stakeOf(b))} → {money(stakeOf(b) * b.odds)}</span>}
                    <span className="sr-add">{added ? '✓' : '+'}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <p className="sr-foot">
        {L('Win chance = the Vegas line (DraftKings, without the book\'s cut) mixed 70/30 with the scout\'s own model (NHL: goals for and against with last season\'s mixed in, the likely starting goalie, back-to-backs, injuries) — for moneylines, spreads, puck / run lines and totals. Other bets: the odds engine\'s model. Value = how much more a bet is worth than the site pays. Max 3 bets per game. Parlays use one leg per game (bets from the same game win or lose together). Stakes: value bets get more (a quarter of the Kelly formula, max 3 units), others 1 unit, long shots and parlays half a unit — the plan never goes over the daily budget.',
          'Chances = la cote de Las Vegas (DraftKings, sans la marge du livre) combinée 70/30 au modèle du scout (LNH : buts pour et contre avec la saison passée, le gardien partant probable, les matchs consécutifs, les blessures) — pour les gagnants, écarts et totaux. Autres paris : le modèle des cotes. Valeur = combien un pari vaut plus que ce que le site paie. Max 3 paris par match. Les combinés prennent une sélection par match (les paris d\'un même match gagnent ou perdent ensemble). Mises : plus sur les paris de valeur (un quart de la formule de Kelly, max 3 unités), 1 unité sinon, une demi-unité pour les gros coups et les combinés — le plan ne dépasse jamais le budget du jour.')}
      </p>
    </div>
  );
}
